import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Card, CardTitle } from '@/components/ui';
import { useTheme } from '@/hooks/useTheme';

interface BrowserChartProps {
  data: { browser: string; clicks: number }[];
  title?: string;
}

const COLORS = ['#D4E157', '#B8A9C9', '#60A5FA', '#F97316', '#64748B', '#EC4899'];

const BrowserChart = ({ data, title }: BrowserChartProps) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const displayTitle = title ?? t('stats.clicksByBrowser');

  const total = data.reduce((sum, item) => sum + item.clicks, 0);

  if (data.length === 0 || total === 0) {
    return (
      <Card variant="default" padding="lg">
        <CardTitle className="mb-6">{displayTitle}</CardTitle>
        <div className="h-[250px] flex items-center justify-center text-gray-400">
          {t('common.noData')}
        </div>
      </Card>
    );
  }

  const series = data.map((item) => item.clicks);
  const labels = data.map(
    (item) => item.browser.charAt(0).toUpperCase() + item.browser.slice(1)
  );

  const options: ApexOptions = {
    chart: {
      type: 'donut',
      background: 'transparent',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    theme: {
      mode: isDark ? 'dark' : 'light',
    },
    colors: COLORS,
    labels,
    legend: {
      position: 'bottom',
      labels: {
        colors: isDark ? '#E5E7EB' : '#1A1A1A',
      },
      formatter: (legendName: string, opts: { seriesIndex: number; w: { globals: { series: number[] } } }) => {
        const value = opts.w.globals.series[opts.seriesIndex];
        const percentage = Math.round((value / total) * 100);
        return `${legendName} (${percentage}%)`;
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
        },
      },
    },
    stroke: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      y: {
        formatter: (value: number) => {
          const percentage = Math.round((value / total) * 100);
          return `${value} (${percentage}%)`;
        },
      },
    },
  };

  return (
    <Card variant="default" padding="lg">
      <CardTitle className="mb-6">{displayTitle}</CardTitle>
      <div className="h-[250px]">
        <Chart
          options={options}
          series={series}
          type="donut"
          height="100%"
          width="100%"
        />
      </div>
    </Card>
  );
};

export default BrowserChart;
